import { compact, isEqual, isPlainObject } from 'lodash';
import { CustomComponentTypes } from '../constants/custom-component-types';
import type {
  DocumentSigningValue,
  IModularBenefitComponent,
  IModularBenefitStep,
} from '../types/modular-benefit';
import { isEmployeeDocumentSigningCompleted } from './document-signing';

export const asDocumentSigningHistory = (
  value: unknown
): DocumentSigningValue[] => {
  if (Array.isArray(value)) {
    return value as DocumentSigningValue[];
  }

  if (isPlainObject(value)) {
    const record = value as Record<string, unknown>;

    if (
      typeof record.signingRequestId === 'number' ||
      record.signedDocumentUrl != null ||
      Array.isArray(record.signers)
    ) {
      return [value as DocumentSigningValue];
    }
  }

  return [];
};

export const getAllModularBenefitComponents = (
  steps: IModularBenefitStep[] = []
): IModularBenefitComponent[] => {
  const mainComponents = steps?.flatMap((step) => step?.components ?? []);
  const subComponents = mainComponents.flatMap((mainComponent) => {
    const contacts = (
      mainComponent as {
        contacts?: { subComponents?: IModularBenefitComponent[] }[];
      }
    ).contacts;

    return contacts?.flatMap((contact) => contact?.subComponents ?? []) ?? [];
  });

  const allComponents = [
    ...mainComponents,
    ...subComponents,
  ] as IModularBenefitComponent[];

  return compact(allComponents);
};

export interface ModularBenefitAdditionalConfigLike {
  steps?: IModularBenefitStep[];
}

export const getMatchingDocumentSigningComponent = (
  userInputComponent: { name: string; type: CustomComponentTypes | string },
  additionalConfig?: ModularBenefitAdditionalConfigLike
): IModularBenefitComponent | undefined => {
  if (!additionalConfig?.steps) {
    return undefined;
  }

  return getAllModularBenefitComponents(additionalConfig.steps).find(
    (configComponent) =>
      configComponent.name === userInputComponent.name &&
      configComponent.type === userInputComponent.type
  );
};

/**
 * Builds one display list for document signing by combining benefit **config**
 * (`component.value` from the API) with **form** state (`benefitForm` / enrolment
 * `user_input`). Form fields win when the same `signingRequestId` exists in both.
 *
 * @param configValue - Raw `DOCUMENT_SIGNING` value from modular benefit config
 *   (array or single object normalised by {@link asDocumentSigningHistory}).
 * @param formValue - Current or persisted user input for that component.
 * @returns Merged requests, then {@link sortDocumentSigningHistory} (awaiting
 *   manager first, then fully signed, etc.).
 *
 * @example
 * // Form empty → show server/config only (e.g. after enrolment, detail page).
 * mergeDocumentSigningDisplayHistory(
 *   [{ signingRequestId: 1, signedDocumentUrl: 'https://x/signed.pdf' }],
 *   null
 * );
 * // → [{ signingRequestId: 1, signedDocumentUrl: 'https://x/signed.pdf' }]
 *
 * @example
 * // Config empty → show in-flow form only (first-time setup before config is populated).
 * mergeDocumentSigningDisplayHistory(
 *   [],
 *   [{ signingRequestId: 2, signers: [{ role: 'EMPLOYEE', order: 0, status: 'COMPLETED' }] }]
 * );
 * // → [employee-completed row from form]
 *
 * @example
 * // Same signingRequestId in both → shallow merge, form overlays config
 * // (e.g. template row from API + employee completion from DocuSeal without URL yet).
 * mergeDocumentSigningDisplayHistory(
 *   [{ signingRequestId: 10, status: 'PENDING', signers: [] }],
 *   [{ signingRequestId: 10, signers: [{ role: 'EMPLOYEE', order: 0, status: 'COMPLETED' }] }]
 * );
 * // → [{ signingRequestId: 10, status: 'PENDING', signers: [EMPLOYEE COMPLETED] }]
 */
export const mergeDocumentSigningDisplayHistory = (
  configValue: unknown,
  formValue: unknown
): DocumentSigningValue[] => {
  const fromConfig = asDocumentSigningHistory(configValue);
  const fromForm = asDocumentSigningHistory(formValue);

  if (fromForm.length === 0) {
    return fromConfig;
  }

  if (fromConfig.length === 0) {
    return fromForm;
  }

  if (
    fromConfig.length === 1 &&
    fromForm.length === 1 &&
    typeof fromConfig[0].signingRequestId !== 'number' &&
    typeof fromForm[0].signingRequestId === 'number'
  ) {
    return [{ ...fromConfig[0], ...fromForm[0] }];
  }

  const consumedFormIds = new Set<number>();
  const result: DocumentSigningValue[] = [];

  for (const configItem of fromConfig) {
    const id = configItem.signingRequestId;

    if (typeof id === 'number') {
      const formMatch = fromForm.find((f) => f.signingRequestId === id);

      if (formMatch) {
        result.push({ ...configItem, ...formMatch });
        consumedFormIds.add(id);
        continue;
      }
    }

    result.push(configItem);
  }

  for (const formItem of fromForm) {
    if (
      typeof formItem.signingRequestId === 'number' &&
      !consumedFormIds.has(formItem.signingRequestId)
    ) {
      result.push(formItem);
    }
  }

  return sortDocumentSigningHistory(result);
};

export const resolveDocumentSigningDisplayHistory = (
  component: IModularBenefitComponent,
  formValue: unknown
): DocumentSigningValue[] => {
  if (component.type !== CustomComponentTypes.DOCUMENT_SIGNING) {
    return [];
  }

  return mergeDocumentSigningDisplayHistory(component.value, formValue);
};

const COMPLETED_SIGNER_STATUS = 'COMPLETED';
const MANAGER_SIGNER_ROLE = 'MANAGER';
const FULLY_SIGNED_STATUS = 'FULLY_SIGNED';
const AWAITING_MANAGER_SIGNATURE_STATUS = 'AWAITING_MANAGER_SIGNATURE';
const AWAITING_MANAGER_SIGN_STATUS = 'AWAITING_MANAGER_SIGN';

const normalizeValue = (value: string | undefined): string =>
  (value ?? '').trim().toUpperCase();

export const isAwaitingManagerSignature = (
  signingRequest: DocumentSigningValue
): boolean => {
  if (signingRequest.signedDocumentUrl) {
    return false;
  }

  const status = normalizeValue(signingRequest.status);

  if (
    status === AWAITING_MANAGER_SIGNATURE_STATUS ||
    status === AWAITING_MANAGER_SIGN_STATUS
  ) {
    return true;
  }

  return (signingRequest.signers ?? []).some(
    (signer) =>
      normalizeValue(signer.role) === MANAGER_SIGNER_ROLE &&
      normalizeValue(signer.status) !== COMPLETED_SIGNER_STATUS
  );
};

export const getDocumentSigningStatusSortPriority = (
  status: string | undefined
): number => {
  const normalized = normalizeValue(status);

  if (
    normalized === AWAITING_MANAGER_SIGNATURE_STATUS ||
    normalized === AWAITING_MANAGER_SIGN_STATUS
  ) {
    return 0;
  }

  if (normalized === FULLY_SIGNED_STATUS) {
    return 1;
  }

  return 2;
};

export const sortDocumentSigningHistory = (
  signingHistory: DocumentSigningValue[]
): DocumentSigningValue[] =>
  [...signingHistory].sort((a, b) => {
    const priorityDiff =
      getDocumentSigningStatusSortPriority(a.status) -
      getDocumentSigningStatusSortPriority(b.status);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return (b.signingRequestId ?? 0) - (a.signingRequestId ?? 0);
  });

interface DocumentSigningSummaryVisibilityParams {
  signingHistory: DocumentSigningValue[];
  currentSigningValue: unknown;
  initialSigningValue: unknown;
  hasChanges?: boolean;
  needsResigning?: boolean;
  hideSummaryWhenHasChangesAndUnchanged?: boolean;
}

interface DocumentSigningSummaryVisibilityReturn {
  summarySigningHistory: DocumentSigningValue[];
  hasRenderableSigningState: boolean;
  signingValueUnchanged: boolean;
  hasSigningValueChanged: boolean;
  showUnsavedSignedOnly: boolean;
  showSummary: boolean;
}

export const getDocumentSigningSummaryVisibility = ({
  signingHistory,
  currentSigningValue,
  initialSigningValue,
  hasChanges = false,
  needsResigning = false,
  hideSummaryWhenHasChangesAndUnchanged = false,
}: DocumentSigningSummaryVisibilityParams): DocumentSigningSummaryVisibilityReturn => {
  const signingValueUnchanged = isEqual(
    currentSigningValue,
    initialSigningValue
  );
  const hasSigningValueChanged = !signingValueUnchanged;
  const currentSigningRequest =
    asDocumentSigningHistory(currentSigningValue)[0];
  const summarySigningHistory = hasSigningValueChanged
    ? signingHistory.filter((request) => !request.signedDocumentUrl)
    : signingHistory;
  const hasRenderableSigningState = summarySigningHistory.some(
    (request) =>
      Boolean(request.signedDocumentUrl) ||
      isAwaitingManagerSignature(request) ||
      isEmployeeDocumentSigningCompleted(request)
  );
  const showUnsavedSignedOnly =
    hasSigningValueChanged &&
    isEmployeeDocumentSigningCompleted(currentSigningRequest);
  const shouldHideSummaryForUnchangedValue =
    hideSummaryWhenHasChangesAndUnchanged &&
    hasChanges &&
    signingValueUnchanged;
  const showSummary =
    hasRenderableSigningState &&
    !needsResigning &&
    !shouldHideSummaryForUnchangedValue;

  return {
    summarySigningHistory,
    hasRenderableSigningState,
    signingValueUnchanged,
    hasSigningValueChanged,
    showUnsavedSignedOnly,
    showSummary,
  };
};
